/**
 * The languages per locales configuration here should resemble the indy-api
 * configuration in config/application.rb.
 */

import { AU as AU_REGION, CB as CB_REGION, NZ as NZ_REGION, region, UK as UK_REGION } from 'src/config'

/** English */
export const EN = 'en'
/** Spanish */
export const ES = 'es'
/** Antigua */
export const EN_AG = 'en-AG'
/** Australia */
export const EN_AU = 'en-AU'
/** United Kingdom */
export const EN_GB = 'en-GB'
/** Guyana */
export const EN_GY = 'en-GY'
/** Saint Kitts and Nevis */
export const EN_KN = 'en-KN'
/** Saint Lucia */
export const EN_LC = 'en-LC'
/** New Zealand */
export const EN_NZ = 'en-NZ'
/** Puerto Rico */
export const EN_PR = 'en-PR'
/** Sint Maarten */
export const EN_SX = 'en-SX'
/** Trinidad and Tobago */
export const EN_TT = 'en-TT'
/** United States */
export const EN_US = 'en-US'
/** U.S. Virgin Islands */
export const EN_VI = 'en-VI'
/** Spain */
export const ES_ES = 'es-ES'
/** Dominican Republic */
export const ES_DO = 'es-DO'
/** Guatemala */
export const ES_GT = 'es-GT'
/** Panama */
export const ES_PA = 'es-PA'
/** Puerto Rico */
export const ES_PR = 'es-PR'
/** United States */
export const ES_US = 'es-US'

/**
 * Some, but not all, locales have translation files that the app can fetch at runtime, stored
 * in the i18n/locales/ directory. Translation lookup is described in more detail in the
 * Notion page "Internationalization and Localization."
 */
export const LOCALES_WITH_TRANSLATION_FILES = [
  EN, EN_AU, EN_GB, EN_US, EN_NZ, ES
]

/**
 * Determines all the available language and language-region locales, and the default locales
 * for the app based on the app mode and app region. This determines the possible set of locales
 * that may be used by the app, but not necessarily the set of locales that are displayed to users.
 *
 * IMPORTANT: The order of language-region locales configured below matters, as the first language-region
 * locales per language are what the app will default to when the user's exact locale is not available.
 */
function computeRegionLocales () {
  const availableLocales = { [EN]: EN, [ES]: ES }
  let defaultLocale = EN_US

  if (region === UK_REGION) {
    defaultLocale = EN_GB
    _.extend(
      availableLocales,
      {
        // Language defaults
        [EN_GB]: EN_GB,
        [ES_ES]: ES_ES
        // Others
      }
    )
  } else if (region === AU_REGION) {
    defaultLocale = EN_AU
    _.extend(
      availableLocales,
      {
        // Language defaults
        [EN_AU]: EN_AU,
        [ES_ES]: ES_ES
        // Others
      }
    )
  } else if (region === NZ_REGION) {
    defaultLocale = EN_NZ
    _.extend(
      availableLocales,
      {
        // Language defaults
        [EN_NZ]: EN_NZ,
        [ES_ES]: ES_ES
        // Others
      }
    )
  } else if (region === CB_REGION) {
    defaultLocale = EN_PR
    _.extend(
      availableLocales,
      {
        // Language defaults
        [EN_PR]: EN_PR,
        [ES_PR]: ES_PR,
        // Others
        [EN_AG]: EN_AG,
        [EN_GY]: EN_GY,
        [EN_KN]: EN_KN,
        [EN_LC]: EN_LC,
        [EN_SX]: EN_SX,
        [EN_TT]: EN_TT,
        [EN_US]: EN_US,
        [EN_VI]: EN_VI,
        [ES_DO]: ES_DO,
        [ES_GT]: ES_GT,
        [ES_PA]: ES_PA
      }
    )
  } else {
    defaultLocale = EN_US
    _.extend(
      availableLocales,
      {
        // Language defaults
        [EN_US]: EN_US,
        [ES_US]: ES_US
        // Others
      }
    )
  }

  /**
   * Available language-only locales (without regions).
   * @type {Object<string, string>}
   */
  const availableLanguages = _.pickBy(availableLocales, (locale, _key) => {
    return _.split(locale, '-').length === 1
  })
  const defaultLanguage = _.split(defaultLocale, '-')[0]

  return { availableLocales, defaultLocale, availableLanguages, defaultLanguage }
}

export const {
  availableLocales: REGION_AVAILABLE_LOCALES,
  defaultLocale: REGION_DEFAULT_LOCALE,
  availableLanguages: REGION_AVAILABLE_LANGUAGES,
  defaultLanguage: REGION_DEFAULT_LANGUAGE
} = computeRegionLocales()

/**
 * This allows us to force certain flags (via ISO 3166-1 alpha-2 country codes) to be displayed
 * for specific locales. Some components display a flag appropriate to the current locale, using
 * the locale's region part. However, some multi-lingual countries have multiple locales, one for
 * each language, and this can cause the same country flag to be displayed for different languages.
 * For example, Puerto Rico might serve `en-PR` and `es-PR`.
 */
export const LOCALE_TO_COUNTRY_FLAG_MAP = Object.freeze({
  // English
  [EN]: 'US',
  [EN_PR]: 'US',
  // Spanish
  [ES]: 'ES',
  [ES_PR]: 'ES',
  [ES_US]: 'ES'
})

/**
 * ISO 3166-1 country codes used to populate Admin Circuit and Site form fields.
 * These values are also used for Google Maps API.
 * TODO: Move this to the API.
 */
export const COUNTRY_CODES = Object.freeze({
  AG: 'AG', // Antigua
  AU: 'AU', // Australia
  AW: 'AW', // Aruba
  BO: 'BO', // Bolivia
  CW: 'CW', // Curacao
  DO: 'DO', // Dominican Republic
  GB: 'GB', // UK
  GP: 'GP', // Guadeloupe
  GT: 'GT', // Guatemala
  GY: 'GY', // Guyana
  KN: 'KN', // Saint Kitts and Nevis
  LC: 'LC', // Saint Lucia
  NZ: 'NZ', // New Zealand
  PA: 'PA', // Panama
  PR: 'PR', // Puerto Rico
  SX: 'SX, MF', // Sint Maarten / Saint Martin
  TT: 'TT', // Trinidad and Tobago
  US: 'US', // USA
  VI: 'VI' // US Virgin Islands
})

export const LANGUAGE_COOKIE_NAME = 'lang'
export const LANGUAGE_COOKIE_EXPIRY_DAYS = 365
